import * as React from 'react';
import styled from 'styled-components';
import ReactTooltip from 'react-tooltip';
import View from '@sdk/components/primitives/View';
import Tooltip from '@sdk/common/tooltip/Tooltip';

import usePremiumGuard from './usePremiumGuard';

const PremiumGuardPointerStyle = styled(View)`
  cursor: pointer;
`;

const StyledPremiumGuard = styled(View)`
  opacity: 0.5;
  pointer-events: none;

  &:not(.active-pointer) > .active-pointer {
    pointer-events: auto !important;
  }
`;

export type PremiumGuardPassedConfig = {
  hasAccess: boolean;
  requiredPlan: string;
  tooltipContent: string;
  tooltipId: string;
};

type PremiumGuardModalProps = {
  premiumFeatureName: string;
  premiumFeature?: string;
};

export type OpenPremiumGuardModal = (props: PremiumGuardModalProps) => void;

type Props = {
  hasAccess: boolean;
  children: JSX.Element;
  premiumFeature?: string;
  premiumGuardModalName?: string;
  tooltipContent?: string;
  tooltipPlace?: string;
  // @TODO: ref to the tooltip
  tooltipRef?: any;
};
const PremiumGuard: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  // PremiumFeature is used to decide what level of subscribtion is needed for access
  premiumFeature,
  // PremiumGuardModalName is used to figure out which info modal to show
  premiumGuardModalName,
  hasAccess,
  tooltipContent,
  tooltipPlace = 'top',
  tooltipRef,
}) => {
  // If user has access do not change the component at all;
  if (hasAccess) {
    return children;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { openPremiumGuardModal } = usePremiumGuard();

  const openPremiumModalHandler = (): void => {
    if (!hasAccess && !!premiumGuardModalName) {
      openPremiumGuardModal({
        premiumFeatureName: premiumGuardModalName,
        premiumFeature,
      });
    }
  };

  if (tooltipContent) {
    return (
      <>
        <PremiumGuardPointerStyle
          onClick={openPremiumModalHandler}
          data-tip={tooltipContent}
          data-place={tooltipPlace}>
          <StyledPremiumGuard>{children}</StyledPremiumGuard>
        </PremiumGuardPointerStyle>
        <Tooltip attachToBody />
      </>
    );
  }

  const showConnectedTooltip = (): void => {
    if (tooltipRef?.current) {
      ReactTooltip.show(tooltipRef.current);
    }
  };

  const hideConnectedTooltip = (): void => {
    if (tooltipRef?.current) {
      ReactTooltip.hide(tooltipRef.current);
    }
  };

  return (
    <PremiumGuardPointerStyle
      onClick={openPremiumModalHandler}
      onMouseEnter={showConnectedTooltip}
      onMouseLeave={hideConnectedTooltip}>
      <StyledPremiumGuard>{children}</StyledPremiumGuard>
    </PremiumGuardPointerStyle>
  );
};

export default PremiumGuard;
